import React, { useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardImg,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap";
import { useGetEmployeeDropDownQuery } from "slices/employee/employeeSlice";
import { useGetProjectsQuery } from "slices/employee/employeeSlice";
import { useGetCompaniesQuery } from "slices/InspectionReport/InspectionReportSlice";
import { BadgeStatus } from "helpers/badgeStatus";
import { STATUS_COLORS } from "helpers/common_helper";
import { formatDateTime } from "helpers/date_utils";
import ImageLightbox from "Components/Common/ImageLightbox";
import { Tooltip } from "react-tooltip";

//SimpleBar

const imageExtensions = ["jpeg", "jpg", "png"];

const fileIconMap: { [key: string]: string } = {
  pdf: "ri-file-pdf-line", // Use an icon for PDFs
  doc: "ri-file-word-line", // Use an icon for Word docs
  docx: "ri-file-word-line",
  zip: "ri-folder-zip-line", // Use an icon for ZIP files
  rar: "ri-folder-zip-line",
  default: "ri-file-line", // Default icon for unknown file types
};

interface ProjectProps {
  getWorkAtHeightPermitById: any;
}

const OverviewTab: React.FC<ProjectProps> = ({ getWorkAtHeightPermitById }) => {
  const { data: employeeData } = useGetEmployeeDropDownQuery();
  const { data: projectData } = useGetProjectsQuery();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get Company Name
  // const getCompanyName = () => {
  //   const company = companyData?.find(
  //     (comp: any) => comp.id === getWorkAtHeightPermitById?.company
  //   );
  //   return company
  //     ? company.company_name
  //     : `Unknown Company ID (${getWorkAtHeightPermitById?.company})`;
  // };

  // Get Project Name
  const getProjectName = () => {
    const project = projectData?.find(
      (proj: any) => proj.id === getWorkAtHeightPermitById?.project
    );
    return project
      ? project.project_name
      : `Unknown Project ID (${getWorkAtHeightPermitById?.project})`;
  };

  // Extract file extension and return corresponding icon or image
  const getFileTypeIconOrImage = (documentUrl: string) => {
    const fileName = documentUrl?.split("/").pop()?.split("?")[0] || "";
    const fileExtension =
      fileName?.split(".").pop()?.toLowerCase() || "default";

    if (imageExtensions.includes(fileExtension)) {
      // Show the actual image if it's an image file
      return (
        <CardImg
          top
          width="150px"
          height="150px"
          src={documentUrl}
          alt={fileName}
          style={{ position: "relative" }}
        />
      );
    } else {
      // Return appropriate icon for non-image files
      const iconClass = fileIconMap[fileExtension] || fileIconMap["default"];
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "150px" }}
        >
          <i className={`${iconClass} ri-4x`} />
        </div>
      );
    }
  };

  const imageDocuments = getWorkAtHeightPermitById?.documents?.filter(
    (doc: any) => doc.attachment?.match(/\.(jpeg|jpg|png|gif)$/i)
  );

  const truncateFileName = (fileName: string, maxLength = 20) => {
    if (fileName.length > maxLength) {
      const truncatedName = fileName.substring(0, maxLength) + "...";
      return truncatedName;
    }
    return fileName;
  };

  const extractDocumentName = (url: string) => {
    const parts = url?.split("/");
    return parts[parts?.length - 1];
  };

  const slides = imageDocuments?.map((doc: any) => ({
    src: doc.attachment_url,
    title: extractDocumentName(doc.attachment),
  }));

  return (
    <React.Fragment>
      <Row>
        <Col xl={12} lg={12}>
          <Card>
            <CardBody>
              <div className="text-muted">
                <h5 className="mt-2">Permit Site Details</h5>
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Project Name :
                        </p>
                        <h5 className="fs-15 mb-0">{getProjectName()}</h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Work Supervisor :
                        </p>
                        <h5 className="fs-15 mb-0">
                          <Badge color="info" className="me-2">
                            {getWorkAtHeightPermitById?.work_supervised_by}
                          </Badge>
                        </h5>
                      </div>
                    </Col>

                    {getWorkAtHeightPermitById?.company_name && (
                      <Col lg={3} sm={6}>
                        <div>
                          <p className="mb-2 text-uppercase fw-medium">
                            Company Name:
                          </p>
                          <h5 className="fs-15 mb-0">
                            {getWorkAtHeightPermitById.company_name}
                          </h5>
                        </div>
                      </Col>
                    )}

                    {/* Conditionally render Sub Company Name only if it exists */}
                    {getWorkAtHeightPermitById?.sub_company_name && (
                      <Col lg={3} sm={6}>
                        <div>
                          <p className="mb-2 text-uppercase fw-medium">
                            Sub Company Name:
                          </p>
                          <h5 className="fs-5 mb-0">
                            {getWorkAtHeightPermitById.sub_company_name}
                          </h5>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>

                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Work Performed By :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.work_performed_by}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Work Location :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.work_location}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Work Description :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.work_description}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Equipment Used :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {
                            getWorkAtHeightPermitById?.type_of_wah_equipment_used
                          }
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Start Date of Work :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.start_date_of_work}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Start Time of Work :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.start_time_of_work}
                        </h5>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          End Date of Work :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.end_date_of_work}
                        </h5>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          End Time of Work :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.end_time_of_work}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Contact Number :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.contact_number}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Submitted Date Time :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {formatDateTime(
                            getWorkAtHeightPermitById?.submit_date,
                            "datetime"
                          )}
                        </h5>
                      </div>
                    </Col>
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Submission Status :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.report_status_name ? (
                            <BadgeStatus
                              value={
                                getWorkAtHeightPermitById?.report_status_name
                              }
                              color={
                                STATUS_COLORS[
                                  getWorkAtHeightPermitById?.report_status_name
                                ]
                              }
                            />
                          ) : null}
                        </h5>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Approval Status :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.approval_status_name ? (
                            <BadgeStatus
                              value={
                                getWorkAtHeightPermitById?.approval_status_name
                              }
                              color={
                                STATUS_COLORS[
                                  getWorkAtHeightPermitById
                                    ?.approval_status_name
                                ]
                              }
                            />
                          ) : null}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row>
                    <Col lg={6} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Report Submitted Location :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {getWorkAtHeightPermitById?.location}
                        </h5>
                      </div>
                    </Col>

                    <Col lg={6} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Report Closed Location :
                        </p>
                        <h5 className="fs-15 mb-0">
                          {/* {getWorkAtHeightPermitById?.location} */}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={12} lg={12}>
          <Card>
            {/* <CardHeader>
              <h5 className="card-title">Task Title: &nbsp;&nbsp;{projectData?.title}</h5>
            </CardHeader> */}
            <CardBody>
              <h5 className="mt-3">Worker And Site Area Details</h5>
              <hr />
              <Row className="mt-4">
                <Col md={12}>
                  <Table className="table-borderless align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Work Location</th>
                        <th scope="col">Employee No</th>
                        <th scope="col">Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getWorkAtHeightPermitById?.work_area_allocation?.length >
                      0 ? (
                        getWorkAtHeightPermitById.work_area_allocation.map(
                          (workArea: any, index: number) => (
                            <tr key={workArea.id}>
                              <td>{workArea.work_location}</td>
                              <td>
                                {workArea.employee?.map(
                                  (location: any, index: number) => {
                                    console.log("employee", location);
                                    return (
                                      <tr className="py-2 d-flex">
                                        {location.employee_number}
                                      </tr>
                                    );
                                  }
                                )}
                              </td>

                              <td>
                                {workArea.employee?.map(
                                  (location: any, index: number) => {
                                    return (
                                      <tr className="py-2 d-flex">
                                        <div className="d-flex align-items-center">
                                          <div className="flex-shrink-0">
                                            <div className="flex-shrink-0 avatar-xs me-2">
                                              <div className="avatar-title bg-success-subtle text-success rounded-circle fs-13">
                                                {location.employee_fullname?.charAt(
                                                  0
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex-grow-1 ms-2 name">
                                            {location.employee_fullname}
                                          </div>
                                        </div>
                                      </tr>
                                    );
                                  }
                                )}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center">
                            No work locations found yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl={12} lg={12}>
          <Card>
            {/* <CardHeader>
              <h5 className="card-title">Task Title: &nbsp;&nbsp;{projectData?.title}</h5>
            </CardHeader> */}
            <CardBody>
              {/* Display Questionnaires in Table */}
              <h5>Check List</h5>
              <hr />
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {getWorkAtHeightPermitById?.questionnaires?.map((q: any) => (
                    <tr key={q.id}>
                      <td>{q.question}</td>
                      <td>
                        <Badge
                          color={q.answer === "Yes" ? "success" : "danger"}
                        >
                          {q.answer}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>

        <Col xl={12} lg={12}>
          <Card>
            {/* <CardHeader>
              <h5 className="card-title">Task Title: &nbsp;&nbsp;{projectData?.title}</h5>
            </CardHeader> */}
            <CardBody>
              {/* Display Documents */}
              <h5>Safety Documents</h5>
              <hr />
              <Row>
                {getWorkAtHeightPermitById?.documents?.map(
                  (document: any, index: number) => {
                    const isImage = document.attachment.match(
                      /\.(jpeg|jpg|png|gif)$/i
                    );

                    return (
                      <Col key={index} md={3} className="mb-4">
                        <Card
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            if (isImage) {
                              const imgIndex = imageDocuments.findIndex(
                                (imgDoc: any) =>
                                  imgDoc.attachment_url ===
                                  document.attachment_url
                              );
                              setCurrentImageIndex(imgIndex);
                              setLightboxOpen(true);
                            }
                          }}
                        >
                          {getFileTypeIconOrImage(document?.attachment_url)}
                          <CardBody>
                            {/* Extract document name from URL */}
                            <CardTitle tag="h5">
                              <span
                                data-tooltip-id={`document-tooltip-${index}`}
                                data-tooltip-content={extractDocumentName(
                                  document?.attachment
                                )}
                              >
                                {truncateFileName(
                                  extractDocumentName(document?.attachment)
                                )}
                              </span>
                              <Tooltip
                                id={`document-tooltip-${index}`}
                                place="top"
                                style={{ zIndex: 9999 }}
                              />
                            </CardTitle>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  }
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
        {/* Add Risk Assessment Section */}
        <Col xl={12} lg={12}>
          <Card>
            <CardBody>
              <h5>Risk Assessment</h5>
              <hr />
              <Table className="table table-striped">
                <thead>
                  <tr>
                    <th>Assessment ID</th>
                    <th>Project Name</th>
                    <th>Report Date</th>
                    <th>Supervised By</th>
                  </tr>
                </thead>
                <tbody>
                  {getWorkAtHeightPermitById?.risk_assessment?.length > 0 ? (
                    getWorkAtHeightPermitById.risk_assessment.map(
                      (assessment: any) => (
                        <tr key={assessment.id}>
                          <td>{assessment.assessment_id}</td>
                          <td>{assessment.project_name}</td>
                          <td>{assessment.report_date}</td>
                          <td>{assessment.supervised_by_name}</td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No risk assessment found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        {/* Add Toolbox Meeting Section */}
        <Col xl={12} lg={12}>
          <Card>
            <CardBody>
              <h5>Toolbox Meeting</h5>
              <hr />
              <Table className="table table-striped">
                <thead>
                  <tr>
                    <th>Toolbox ID</th>
                    <th>Project Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getWorkAtHeightPermitById?.toolbox_meeting?.length > 0 ? (
                    getWorkAtHeightPermitById.toolbox_meeting.map(
                      (meeting: any) => (
                        <tr key={meeting.id}>
                          <td>{meeting.toolbox_id}</td>
                          <td>{meeting.project_name}</td>
                          <td>{meeting.start_date_of_work}</td>
                          <td>{meeting.end_date_of_work}</td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No toolbox meetings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        <ImageLightbox
          open={lightboxOpen}
          slides={slides}
          currentIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      </Row>
    </React.Fragment>
  );
};

export default OverviewTab;
